import React, { useEffect, useState, useContext, createContext } from "react";
import appApi from "../../api";
import Button from "../UserInput/Buttons/Button";
import ErrorMessageBox from "../../components/ErrorMessageBox";

const editContext = createContext();

const EditableField = ({ fieldName, labelText, fullWidth, filter, getSavedValueCallback = null }) => {
    const { edit, setEdit, scratchData, setScratchData, error, save, cancelEdit } = useContext(editContext);

    const handleBlur = (e) => {
        if (filter) {
            const modifiedValue = filter(e.target.value);
            setScratchData((data) => ({
                ...data,
                [fieldName]: modifiedValue,
            }));
        }
    };

    return (
        <div className="flex flex-col items-start mb-4">
            <div className="font-semibold text-slate-600 mb-2">{labelText}</div>
            <div className="flex items-center justify-between w-full">
                <div className={`flex items-center space-x-[26px] ${fullWidth ? "flex-1" : ""}`}>
                    <input
                        type="text"
                        readOnly={!edit[fieldName]}
                        value={scratchData[fieldName] ? scratchData[fieldName] : ""}
                        onChange={(e) =>
                            setScratchData((data) => ({
                                ...data,
                                [fieldName]: e.target.value,
                            }))
                        }
                        onBlur={handleBlur}
                        className={`text-field ${fullWidth ? "flex-1" : ""}`}
                    />
                    <div className="space-x-2">
                        {edit[fieldName] ? (
                            <div className="flex space-x-2">
                                <Button size="xs" handleOnClick={() => save(fieldName, getSavedValueCallback)}>
                                    Save
                                </Button>
                                <Button size="xs" colorTheme="secondary" handleOnClick={() => cancelEdit(fieldName)}>
                                    Cancel
                                </Button>
                            </div>
                        ) : (
                            <Button size="xs" handleOnClick={() => setEdit((edit) => ({ ...edit, [fieldName]: true }))}>
                                Edit
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            <div className={`${error[fieldName] ? "mb-[-20px]" : ""}`}>
                <ErrorMessageBox error={error[fieldName]} />
            </div>
        </div>
    );
};

const AccountInfo = () => {
    const [savedData, setSavedData] = useState({
        scheduling_user_id: null,
        scheduling_service_id: null,
        scheduling_url: "",
        scheduling_api_key: "",
        scheduling_api_secret: "",
    });

    const [scratchData, setScratchData] = useState({ ...savedData });

    const [edit, setEdit] = useState(Object.fromEntries(Object.keys(scratchData).map((key) => [key, false])));

    const [error, setError] = useState(Object.fromEntries(Object.keys(scratchData).map((key) => [key, ""])));

    async function getCoconutSchedulingInfo() {
        const response = await appApi.get("/company/coconut-scheduling-info");

        const data = {
            scheduling_user_id: response.data?.user_id,
            scheduling_service_id: response.data?.service_id,
            scheduling_url: response.data?.url,
            scheduling_api_key: response.data?.api_key,
            scheduling_api_secret: response.data?.api_secret,
        };

        setSavedData(() => data);
        setScratchData(() => data);
        setEdit(() => Object.fromEntries(Object.keys(data).map((key) => [key, false])));
        setError(() => Object.fromEntries(Object.keys(data).map((key) => [key, ""])));
    }

    async function save(field, getSavedValueCallback = null) {
        // Create a subset object with only the specified field
        const dataToSend = { [field]: scratchData[field] };

        try {
            const response = await appApi.put("/company/coconut-scheduling-info", dataToSend);

            setSavedData((prevData) => ({
                ...prevData,
                [field]: getSavedValueCallback ? getSavedValueCallback(dataToSend) : response.data.data[field],
            }));
            setEdit((prevEdit) => ({ ...prevEdit, [field]: false }));
            setError((prevError) => ({ ...prevError, [field]: "" }));
        } catch (error) {
            setError((prevError) => ({ ...prevError, [field]: error.response?.data.error }));
        }
    }

    function cancelEdit(field) {
        setScratchData((data) => ({ ...data, [field]: savedData[field] }));
        setEdit((edit) => ({ ...edit, [field]: false }));
        setError((error) => ({ ...error, [field]: "" }));
    }

    useEffect(() => {
        getCoconutSchedulingInfo();
    }, []);

    const sanitizeURL = (url) => {
        //remove spaces and trailing slashes
        const sanitizedURL = url
            .replace(/\s+/g, "") //remove invalid blank chars
            .replace(/\/+$/, ""); //remove trailing slashes
        return sanitizedURL;
    };

    return (
        <editContext.Provider value={{ edit, setEdit, scratchData, setScratchData, error, save, cancelEdit }}>
            <div className="flex-1 py-8 bg-white border border-slate-100 rounded-[10px]">
                <div
                    className="mx-[38px]  font-[600] text-[20.7px] text-[#455468] mb-[21px] "
                    style={{ minWidth: "390px" }}
                >
                    Company Information
                </div>
                <div className="px-[38px] mt-4">
                    <EditableField
                        fieldName="scheduling_user_id"
                        labelText="Coconut User Id"
                        filter={(input) => input.trim()}
                        getSavedValueCallback={(data) => data.scheduling_user_id}
                    />
                    <EditableField
                        fieldName="scheduling_service_id"
                        labelText="Coconut Service Id"
                        filter={(input) => input.trim()}
                        getSavedValueCallback={(data) => data.scheduling_service_id}
                    />
                    <EditableField
                        fieldName="scheduling_url"
                        labelText="Coconut Scheduling URL"
                        filter={sanitizeURL}
                        getSavedValueCallback={(data) => data.scheduling_url}
                        fullWidth
                    />
                    <EditableField
                        fieldName="scheduling_api_key"
                        labelText="Coconut API Key"
                        filter={(input) => input.trim()}
                        getSavedValueCallback={(data) => data.scheduling_api_key}
                        fullWidth
                    />
                    <EditableField
                        fieldName="scheduling_api_secret"
                        labelText="Coconut API Secret Key"
                        filter={(input) => input.trim()}
                        getSavedValueCallback={(data) => data.scheduling_api_secret}
                        fullWidth
                    />
                </div>
            </div>
        </editContext.Provider>
    );
};

export default AccountInfo;
